import React, { useState, useEffect } from 'react';
import {Mail, MessageCircleDashed as MessageCircle, Clock, CheckCircle, AlertCircle, HelpCircle, ExternalLink} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-hot-toast';

export default function Help() {
  const { user } = useAuth();
  const [isSending, setIsSending] = useState(false);
  const [showLiveSupport, setShowLiveSupport] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');

  // Vérifier si on est dans les 48h post-lancement (31 déc 2024 = date de lancement)
  useEffect(() => {
    const launchDate = new Date('2024-12-31T00:00:00');
    const now = new Date();
    const hoursSinceLaunch = (now.getTime() - launchDate.getTime()) / (1000 * 60 * 60);
    
    if (hoursSinceLaunch <= 48) {
      setShowLiveSupport(true);
    }
  }, []);

  const handleResendCode = async () => {
    if (!user?.email) {
      toast.error("Vous devez être connecté pour renvoyer le code d'accès");
      return;
    }

    setIsSending(true);
    try {
      const { lumi } = await import('../lib/lumi');
      await lumi.functions.invoke('sendAccessCodeEmail', {
        method: 'POST'
      });
      toast.success(
        "Code d'accès renvoyé ! Vérifiez votre boîte mail.\n\nℹ️ Pensez à vérifier vos spams.",
        { duration: 6000, icon: '✉️' }
      );
    } catch (err: any) {
      console.error('Resend failed:', err);
      toast.error(
        err?.data?.error || "Erreur lors du renvoi. Contactez le support.",
        { duration: 5000 }
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleSendSupportMessage = () => {
    if (!supportMessage.trim()) {
      toast.error("Veuillez entrer un message");
      return;
    }

    const subject = encodeURIComponent("Support Body Titan - Code d'accès");
    const body = encodeURIComponent(
      `Message de ${user?.email || 'utilisateur non connecté'}:\n\n${supportMessage}\n\n---\nEnvoyé depuis l'application Body Titan`
    );
    
    window.location.href = `mailto:bodytitan64@yahoo.com?subject=${subject}&body=${body}`;
    toast.success("Client email ouvert. Envoyez votre message depuis votre application email.");
    setSupportMessage('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* En-tête */}
        <div className="text-center mb-12">
          <HelpCircle className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-500 via-white to-blue-500 bg-clip-text text-transparent">
            Centre d'Aide
          </h1>
          <p className="text-xl text-gray-300">
            Guide complet pour accéder à votre espace Body Titan
          </p>
        </div>

        {/* Support Live 48h */}
        {showLiveSupport && (
          <div className="bg-gradient-to-r from-green-600/20 to-green-400/20 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-green-500/30">
            <div className="flex items-start gap-4">
              <Clock className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white mb-2">
                  🎉 Support Prioritaire Actif
                </h2>
                <p className="text-gray-300 mb-4">
                  Durant les 48 premières heures suivant le lancement, notre équipe est disponible pour vous aider rapidement avec tout problème d'accès.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <a
                    href="mailto:bodytitan64@yahoo.com"
                    className="flex items-center gap-3 bg-gray-800/50 hover:bg-gray-700/50 p-4 rounded-lg transition-all border border-gray-600"
                  >
                    <Mail className="w-6 h-6 text-blue-400" />
                    <div>
                      <p className="font-semibold text-white">Email Support</p>
                      <p className="text-sm text-gray-400">bodytitan64@yahoo.com</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-3 bg-gray-800/50 p-4 rounded-lg border border-gray-600">
                    <MessageCircle className="w-6 h-6 text-green-400" />
                    <div>
                      <p className="font-semibold text-white">Réponse rapide</p>
                      <p className="text-sm text-gray-400">Sous 2-4 heures</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Guide principal */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-gray-700">
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
            <AlertCircle className="w-8 h-8 text-blue-500" />
            Que faire si vous n'avez pas reçu votre code ?
          </h2>

          <div className="space-y-6">
            {/* Étape 1 */}
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
              <div className="flex items-start gap-4">
                <div className="bg-blue-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-3">
                    Vérifiez vos spams / courrier indésirable
                  </h3>
                  <p className="text-gray-300 mb-4">
                    La plupart du temps, l'email se trouve dans le dossier "Spam" ou "Courrier indésirable". 
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-400">
                    <li>Ouvrez votre application email (Gmail, Outlook, etc.)</li>
                    <li>Recherchez dans le dossier "Spam" ou "Indésirables"</li>
                    <li>Cherchez un email de "Body Titan" ou "Naturaltitan Physique"</li>
                    <li>Si vous le trouvez, marquez-le comme "Non spam"</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Étape 2 */}
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
              <div className="flex items-start gap-4">
                <div className="bg-blue-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-3">
                    Vérifiez l'adresse email utilisée
                  </h3>
                  <p className="text-gray-300 mb-4">
                    Assurez-vous que vous vérifiez la bonne adresse email :
                  </p>
                  {user?.email ? (
                    <div className="bg-gray-800/50 p-4 rounded-lg border border-blue-500/30">
                      <p className="text-sm text-gray-400 mb-1">Votre compte actuel :</p>
                      <p className="font-mono text-blue-400 font-semibold">{user.email}</p>
                    </div>
                  ) : (
                    <div className="bg-yellow-600/20 p-4 rounded-lg border border-yellow-500/30">
                      <p className="text-yellow-300">
                        ⚠️ Vous n'êtes pas connecté. Connectez-vous d'abord pour vérifier votre email.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Étape 3 */}
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
              <div className="flex items-start gap-4">
                <div className="bg-blue-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-3">
                    Renvoyez votre code d'accès
                  </h3>
                  <p className="text-gray-300 mb-4">
                    Si vous ne trouvez toujours pas l'email, renvoyez-le :
                  </p>
                  <button
                    onClick={handleResendCode}
                    disabled={isSending || !user}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSending ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        Renvoyer mon code maintenant
                      </>
                    )}
                  </button>
                  {!user && (
                    <p className="text-sm text-yellow-400 mt-3">
                      ⚠️ Vous devez être connecté pour renvoyer le code
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Étape 4 */}
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
              <div className="flex items-start gap-4">
                <div className="bg-blue-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  4
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-3">
                    Patientez 5-10 minutes
                  </h3>
                  <p className="text-gray-300">
                    Après avoir cliqué sur "Renvoyer", attendez quelques minutes. L'email peut prendre jusqu'à 10 minutes pour arriver dans certains cas.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Rapide */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-6">Questions Fréquentes</h2>
          
          <div className="space-y-4">
            <details className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
              <summary className="font-semibold text-white cursor-pointer">
                Combien de temps le code est-il valide ?
              </summary>
              <p className="text-gray-400 mt-3">
                Votre code d'accès est valide indéfiniment une fois généré. Il n'expire pas.
              </p>
            </details>

            <details className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
              <summary className="font-semibold text-white cursor-pointer">
                Puis-je avoir plusieurs codes ?
              </summary>
              <p className="text-gray-400 mt-3">
                Non, chaque email ne dispose que d'un seul code d'accès unique lié à votre abonnement.
              </p>
            </details>

            <details className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
              <summary className="font-semibold text-white cursor-pointer">
                L'email vient de quelle adresse ?
              </summary>
              <p className="text-gray-400 mt-3">
                L'email provient du système Body Titan avec l'objet : "🔑 Votre accès Naturaltitan Physique"
              </p>
            </details>

            <details className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
              <summary className="font-semibold text-white cursor-pointer">
                Que faire si rien ne fonctionne ?
              </summary>
              <p className="text-gray-400 mt-3">
                Contactez directement le support à bodytitan64@yahoo.com en précisant votre adresse email d'inscription.
              </p>
            </details>
          </div>
        </div>

        {/* Contact Support */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <MessageCircle className="w-7 h-7 text-blue-500" />
            Besoin d'aide supplémentaire ?
          </h2>
          
          <p className="text-gray-300 mb-6">
            Si vous rencontrez toujours des difficultés, notre équipe est là pour vous aider.
          </p>

          <div className="space-y-4">
            <textarea
              value={supportMessage}
              onChange={(e) => setSupportMessage(e.target.value)}
              placeholder="Décrivez votre problème ici..."
              rows={4}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            
            <button
              onClick={handleSendSupportMessage}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Contacter le Support par Email
            </button>

            <div className="bg-blue-600/20 p-4 rounded-lg border border-blue-500/30">
              <p className="text-sm text-blue-300 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Email de support : <strong>bodytitan64@yahoo.com</strong>
              </p>
              {showLiveSupport && (
                <p className="text-sm text-green-300 mt-2 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Réponse prioritaire sous 2-4 heures (48h post-lancement)
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Retour */}
        <div className="text-center mt-8">
          <a
            href="/exercices"
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
            Retour aux Exercices
          </a>
        </div>
      </div>
    </div>
  );
}
